// Consent model + cookie codec — shared by the marketing site (Astro), the
// consumer app (React), and the API. docs/replan-v2.md R8.
//
// Three categories (functional is always on and not user-toggleable), plus
// per-vendor granularity under `marketing` for the ad networks. The cookie is
// first-party per surface (fajrarabi.com and app.fajrarabi.com each show the
// banner once — deliberate: PDPL disclosure happens on both surfaces).

export type AdVendor = 'meta' | 'google' | 'tiktok' | 'snap';

export interface ConsentChoices {
  functional: true;
  analytics: boolean;
  marketing: boolean;
  /** Granular per-network opt-outs. Absent vendor = follow `marketing`. */
  perVendor?: Partial<Record<AdVendor, boolean>>;
}

/** Bump when the lawyer-reviewed policy text lands (replaces the placeholder). */
export const CONSENT_POLICY_VERSION = '2026-08-draft';

export const CONSENT_COOKIE = 'fa_consent';
export const CONSENT_COOKIE_MAX_AGE_DAYS = 365;

/** localStorage key for the stable anonymous id (pre-auth event stitching). */
export const ANONYMOUS_ID_KEY = 'fa_aid';

export interface StoredConsent {
  /** Policy version the visitor consented to. */
  v: string;
  c: ConsentChoices;
  /** Unix ms of the decision. */
  at: number;
}

export function serializeConsent(stored: StoredConsent): string {
  return encodeURIComponent(JSON.stringify(stored));
}

export function parseConsent(raw: string | undefined | null): StoredConsent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const p = parsed as { v?: unknown; c?: unknown; at?: unknown };
    if (typeof p.v !== 'string' || typeof p.at !== 'number') return null;
    const c = p.c as { analytics?: unknown; marketing?: unknown; perVendor?: unknown } | undefined;
    if (!c || typeof c.analytics !== 'boolean' || typeof c.marketing !== 'boolean') return null;
    return {
      v: p.v,
      at: p.at,
      c: {
        functional: true,
        analytics: c.analytics,
        marketing: c.marketing,
        perVendor: c.perVendor ?? undefined,
      },
    };
  } catch {
    return null;
  }
}

/** Read + parse the consent cookie from a `document.cookie` string. */
export function consentFromCookieString(cookieString: string): StoredConsent | null {
  const match = cookieString.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]+)`));
  return parseConsent(match?.[1]);
}

/** Whether a specific ad vendor may fire. Follows `marketing` unless the
 * visitor opted that vendor out individually. */
export function vendorAllowed(consent: StoredConsent | null, vendor: AdVendor): boolean {
  if (!consent || !consent.c.marketing) return false;
  const perVendor = consent.c.perVendor;
  if (perVendor && perVendor[vendor] === false) return false;
  return true;
}
